import { Lock } from "lucide-react";
import { Fragment } from "react";
import { markProtected } from "../lib/diff";

/**
 * DOC PAGE — the page on the desk.
 *
 * This product compiles a 39-page concept note for a funder, and until now NOTHING in it
 * looked like a document. The Library inspector set the real excerpt in serif — correct — but
 * on the same flat white ground as the chrome, at the same --panel-pad, with no page edge and
 * no shadow, so the artefact read as a paragraph in a settings card. The screen it replaced
 * only *seemed* to do better because it drew a page-shaped object — and that object was three
 * grey <i> skeleton bars. A fake page.
 *
 * DocPage is the honest version of that object: a real sheet of --paper carrying the run's
 * REAL content, lifted off a --surface-2 desk by --elev-doc. Every glyph on it comes from the
 * run. There is no skeleton bar, no placeholder line, no invented metadata.
 *
 * WHAT IT MAY AND MAY NOT DO
 *  - The eyebrow is the ORGANIZATION, in --amber-deep, because amber is this product's colour
 *    for PROTECTED and the page is the protected artefact.
 *  - Protected facts render as SEALED CHIPS (the shared .doc-protected), inside the body text
 *    and again in the footer, so an operator can SEE that the machine did not touch the
 *    numbers, dates, currencies and citations.
 *  - The body fades out via --fade-mask when `continues` is set. A masked excerpt SHOWS its
 *    continuation; it never asserts one with a fabricated ellipsis.
 *  - It renders only what it is given. Omit `subtitle` and there is no subtitle — it will not
 *    manufacture "39 pages" or any other metadata it was not handed.
 *
 * It lives in components/, not in a screen, because /library, /review and the /new empty state
 * must all speak one language about what a document looks like. A screen may not invent a card.
 */

/**
 * Protected facts — numbers, dates, currencies, citations, URLs — as sealed chips. Byte-
 * identical treatment (and title attribute) to the diff's, because the two surfaces must make
 * the same promise in the same words.
 */
export function ProtectedText({ value }: { value: string }) {
  return (
    <>
      {markProtected(value).map((piece, index) =>
        piece.protected ? (
          <mark className="doc-protected" title="Protected fact — never altered" key={index}>
            {piece.value}
          </mark>
        ) : (
          <Fragment key={index}>{piece.value}</Fragment>
        )
      )}
    </>
  );
}

export function DocPage({
  org,
  title,
  subtitle,
  body,
  continues,
  note,
  protectedFacts = [],
  testId,
  bodyTestId,
  footTestId
}: {
  /** The organization this page was personalized for. Omit on the canonical, unpersonalized note. */
  org?: string;
  title: string;
  subtitle?: string;
  /** The document's real first editable paragraph. Never placeholder text. */
  body?: string;
  /** The body is an excerpt and continues in the full document: fade, never an ellipsis. */
  continues?: boolean;
  /** A caption BELOW the body naming what this page is. Chrome, not document copy. */
  note?: string;
  protectedFacts?: string[];
  testId?: string;
  bodyTestId?: string;
  footTestId?: string;
}) {
  return (
    <article className="doc-page" data-testid={testId}>
      <header className="doc-page-head">
        {org ? <p className="doc-page-eyebrow">{org}</p> : null}
        <h3 className="doc-page-title">{title}</h3>
        {subtitle ? <p className="doc-page-subtitle">{subtitle}</p> : null}
      </header>

      {body ? (
        <p
          className="doc-page-body"
          data-continues={continues ? "true" : "false"}
          data-testid={bodyTestId}
        >
          <ProtectedText value={body} />
        </p>
      ) : null}

      {note ? <p className="doc-page-note">{note}</p> : null}

      {protectedFacts.length ? (
        <footer className="doc-page-foot" data-testid={footTestId}>
          <Lock aria-hidden="true" />
          <span className="doc-page-foot-copy">
            <span className="doc-page-foot-label">Protected facts, never altered</span>
            <span className="doc-page-facts">
              {protectedFacts.map((fact) => (
                <span className="doc-page-fact" key={fact}>
                  <ProtectedText value={fact} />
                </span>
              ))}
            </span>
          </span>
        </footer>
      ) : null}
    </article>
  );
}
